import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import {
  getMentoringProgramReflectionQuestions,
  getMentoringProgramReflectionResponses,
} from 'redux/actions/mentoring-program-reflections';
import {
  selectMentoringProgramReflectionQuestions,
  selectMentoringProgramReflectionParticipantResponses,
} from 'redux/selectors/mentoring-program-reflections';
import { MentoringProgramReflectionParticipantQuestions } from 'redux/schemas/models/mentoring-program-reflections';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import useConnection from 'athena/hooks/use-connection';
import { largeSpacing } from 'styles/global_defaults/scaffolding';
import ReflectionReadView from './reflection-read-view';
import ReflectionForm from './reflection-form';
import ReflectionResponseCard from './reflection-response-card';

const wrapperStyles = css`
  gap: ${largeSpacing}px;
`;

type ReflectionContentProps = {
  programId: number;
  connectionId: number;
};

const ReflectionContent = ({ programId, connectionId }: ReflectionContentProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const currentEnrollmentId = mentoringProgram?.mentorshipProgramEnrollment;

  const { connection, mentorshipProgramEnrollment } = useConnection(currentEnrollmentId, connectionId);

  const currentEnrollmentUser = mentorshipProgramEnrollment?.user;
  const currentEnrollmentRole = mentorshipProgramEnrollment?.role?.name;
  const connectionEnrollmentUser = connection?.assignedEnrollment?.user;
  const connectionEnrollmentRole = connection?.assignedEnrollment?.role?.name;

  const participantResponses = useSelector((state) =>
    selectMentoringProgramReflectionParticipantResponses(state, connectionId)
  );
  const questions = useSelector((state) =>
    selectMentoringProgramReflectionQuestions(state, connectionId)
  );

  const currentEnrollmentQuestions = useMemo(
    () => questions?.find((pq: MentoringProgramReflectionParticipantQuestions) => pq.mentorshipProgramEnrollmentId === currentEnrollmentId)?.questions ?? [],
    [questions, currentEnrollmentId],
  );
  const connectionEnrollmentQuestions = useMemo(
    () => questions?.find((pq: MentoringProgramReflectionParticipantQuestions) => pq.mentorshipProgramEnrollmentId !== currentEnrollmentId)?.questions ?? [],
    [questions, currentEnrollmentId],
  );

  const currentEnrollmentResponses = useMemo(
    () => participantResponses?.data?.find(r => r.mentorshipProgramEnrollmentId === currentEnrollmentId) ?? null,
    [participantResponses, currentEnrollmentId],
  );

  const connectionEnrollmentResponses = useMemo(
    () => participantResponses?.data?.find(r => r.mentorshipProgramEnrollmentId !== currentEnrollmentId) ?? null,
    [participantResponses, currentEnrollmentId],
  );

  useEffect(() => {
    dispatch(getMentoringProgramReflectionResponses({ programId, connectionId }));
    dispatch(getMentoringProgramReflectionQuestions({ programId, connectionId }));
  }, [programId, connectionId, dispatch]);

  if (!participantResponses || participantResponses.isLoading || questions === null) return null;

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE.SHARED_REFLECTIONS;

  const heading = (
    <div className='text-center'>
      <p className='heading-2 black mb-1'>{translationBase.HEADING()}</p>
      <p className='heading-5 gray-1 mb-0'>{translationBase.SUBHEADING()}</p>
    </div>
  );

  if (currentEnrollmentResponses?.answers?.length) {
    return (
      <div css={wrapperStyles} className='d-flex flex-column mt-5'>
        {heading}
        <ReflectionReadView
          currentEnrollmentQuestions={currentEnrollmentQuestions}
          connectionEnrollmentQuestions={connectionEnrollmentQuestions}
          currentEnrollmentResponses={currentEnrollmentResponses}
          connectionEnrollmentResponses={connectionEnrollmentResponses}
          currentEnrollmentUser={currentEnrollmentUser}
          currentEnrollmentRole={currentEnrollmentRole}
          connectionEnrollmentUser={connectionEnrollmentUser}
          connectionEnrollmentRole={connectionEnrollmentRole}
        />
      </div>
    );
  }

  if (!currentEnrollmentQuestions.length) return null;

  const sortedQuestions = [...connectionEnrollmentQuestions].sort((a, b) => a.questionIndex - b.questionIndex);
  const connectionAnswersByQuestionId = Object.fromEntries(
    (connectionEnrollmentResponses?.answers ?? []).map(a => [a.questionId, a.response]),
  );

  return (
    <div css={wrapperStyles} className='d-flex flex-column mt-5'>
      {heading}
      {connectionEnrollmentResponses?.answers?.length > 0 && (
        <>
          {sortedQuestions.map(({ id, questionText }, questionIndex) => {
            const answer = connectionAnswersByQuestionId[id];
            if (!answer || !connectionEnrollmentUser) return null;
            return (
              <ReflectionResponseCard
                key={id}
                questionText={questionText}
                response={answer}
                user={connectionEnrollmentUser}
                role={connectionEnrollmentRole}
                index={questionIndex}
              />
            );
          })}
          <div className='d-flex align-items-center w-100'>
            <div className='flex-grow-1 border-top' />
            <span className='mx-2 text-xs semi-bold gray-2 text-uppercase'>
              {translationBase.NOW_IT_IS_YOUR_TURN()}
            </span>
            <div className='flex-grow-1 border-top' />
          </div>
        </>
      )}
      <ReflectionForm
        programId={programId}
        connectionId={connectionId}
        questions={currentEnrollmentQuestions}
        connectionFirstName={connectionEnrollmentUser?.firstName ?? ''}
      />
    </div>
  );
};

export default ReflectionContent;
